import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Logo from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

const inputCls =
  "mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#e60000] py-3 text-white outline-none transition-colors";
const primaryBtn =
  "w-full bg-[#e60000] text-white py-4 text-sm tracking-[0.14em] uppercase font-medium hover:bg-[#990000] transition-colors disabled:opacity-60";
const textBtn = "text-xs tracking-[0.14em] uppercase text-white/60 hover:text-[#e60000] transition-colors";

function ForgotPassword({ initialEmail, onBack }) {
  const [email, setEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      toast.error(err.response ? "Something went wrong. Please try again." : "The server could not be reached. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="forgot-password">
      <h1 className="font-display text-4xl font-black tracking-tight text-white">Reset password</h1>
      {sent ? (
        <>
          <p role="status" data-testid="forgot-password-sent" className="mt-6 text-white/80 text-sm leading-relaxed border-l-2 border-[#e60000] pl-4">
            {sent} The link expires in 30 minutes.
          </p>
          <button type="button" onClick={onBack} className={`mt-10 ${textBtn}`} data-testid="forgot-back">Back to sign in</button>
        </>
      ) : (
        <>
          <p className="mt-2 text-regera-dim text-sm">Enter the admin email address. We will email a link to choose a new password.</p>
          <form onSubmit={submit} className="mt-10 space-y-8">
            <label className="block">
              <span className="overline">Email</span>
              <input data-testid="forgot-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="admin@regera.biz" autoComplete="email" />
            </label>
            <button type="submit" data-testid="forgot-submit" disabled={sending} className={primaryBtn}>
              {sending ? "Sending…" : "Send reset link"}
            </button>
          </form>
          <button type="button" onClick={onBack} className={`mt-8 ${textBtn}`} data-testid="forgot-back">Back to sign in</button>
        </>
      )}
    </div>
  );
}

export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => {
    document.title = "Admin — REGERA";
    if (user) nav("/admin");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      nav("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      let message = "Invalid email or password.";
      if (!err.response) {
        message = "The server could not be reached. Please try again in a moment.";
      } else if (typeof detail === "string") {
        message = detail;
      } else if (Array.isArray(detail)) {
        message = "Please enter a valid email address and password.";
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-[#0a0a0a]" data-testid="admin-login">
      <div className="grain-overlay" aria-hidden />
      <div className="w-full max-w-md">
        <Logo size="admin" className="mb-10" />
        {forgot ? (
          <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />
        ) : (
          <>
            <h1 className="font-display text-4xl font-black tracking-tight text-white">CMS Access</h1>
            <p className="mt-2 text-regera-dim text-sm">Manage portfolio, services, industries, insights and enquiries.</p>
            <form onSubmit={submit} className="mt-10 space-y-8">
              <label className="block">
                <span className="overline">Email</span>
                <input
                  data-testid="admin-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="admin@regera.biz"
                />
              </label>
              <label className="block">
                <span className="overline">Password</span>
                <input
                  data-testid="admin-password"
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="••••••••"
                />
              </label>
              <button type="submit" data-testid="admin-login-submit" disabled={loading} className={primaryBtn}>
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
            <button type="button" onClick={() => setForgot(true)} className={`mt-8 ${textBtn}`} data-testid="forgot-password-link">
              Forgot password?
            </button>
          </>
        )}
      </div>
    </div>
  );
}
